import { ChangeEventType } from '@awais4123/fillify-design-library/lib/common.types';
import { UseFormBehaviorsOnChangeFnType } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { SetStateAction, useCallback, useState } from 'react';

const filterUniquePositionIds = (arr: string[]): string[] => {
  const uniqueSet = new Set(arr);

  const filteredPositionIds = Array.from(uniqueSet).filter((str) => {
    const num = Number(str);
    return !isNaN(num) && num > 0;
  });

  return filteredPositionIds;
};

export const useJanePositionDropdownHandlers = ({
  onChange,
  positionIdsByDepartments,
  primaryPositionId,
  secondaryPositionIds,
  secondaryPositionsFormName,
  setShowDeactivateConfirmModal,
  setNumberOfPositionsDeleted,
}: {
  onChange: UseFormBehaviorsOnChangeFnType;
  positionIdsByDepartments: { [key: string]: string[] };
  primaryPositionId: string;
  secondaryPositionIds: string[];
  secondaryPositionsFormName: string;
  setShowDeactivateConfirmModal: Nullable<(value: SetStateAction<boolean>) => void>;
  setNumberOfPositionsDeleted?: (value: SetStateAction<number>) => void;
}) => {
  const [removedPositions, setRemovedPositions] = useState<string[]>([]);

  const areAllPositionsSelected = useCallback(
    (value, targetValues) => {
      const departmentPositions = Object.entries(positionIdsByDepartments).find(
        ([department, positionIds]) => positionIds.includes(value) || department === value,
      );

      if (departmentPositions) {
        const [department, positionIds] = departmentPositions;
        const filteredPositionIds = positionIds.filter((id) => id !== primaryPositionId);
        const allPositionsSelected = filteredPositionIds.every((id) => targetValues.includes(id));

        if (!allPositionsSelected) {
          return value !== department;
        }
      }

      return true;
    },
    [primaryPositionId, positionIdsByDepartments],
  );

  const handlePrimaryPosition = useCallback(
    (e: ChangeEventType) => {
      const primaryPositionId = `${e.target.value[0]}`;

      const updatedSecondaryPositions = secondaryPositionIds.filter((item) => {
        if (item === primaryPositionId) {
          return false;
        }

        return areAllPositionsSelected(item, secondaryPositionIds);
      });

      const finalSecondaryPositions = updatedSecondaryPositions.filter((item) => {
        const departmentPositions = Object.entries(positionIdsByDepartments).find(
          ([department, positionIds]) => positionIds.includes(item) || department === item,
        );

        if (departmentPositions) {
          const [department, positionIds] = departmentPositions;
          const filteredPositionIds = positionIds.filter((id) => id !== primaryPositionId);
          const allPositionsSelected = filteredPositionIds.every((id) =>
            updatedSecondaryPositions.includes(id),
          );

          if (!filteredPositionIds.length) return false;

          if (!allPositionsSelected) {
            return item !== department;
          }
        }

        return true;
      });

      onChange([
        { ...e },
        {
          target: {
            name: secondaryPositionsFormName,
            value: finalSecondaryPositions,
          },
        },
      ]);
    },
    [
      areAllPositionsSelected,
      onChange,
      positionIdsByDepartments,
      secondaryPositionIds,
      secondaryPositionsFormName,
    ],
  );

  const handleRemovedSecondaryPosition = useCallback(
    (e) => {
      (onChange as ChangeEventType)({
        target: {
          name: secondaryPositionsFormName,
          value: removedPositions,
        },
      });
      if (setShowDeactivateConfirmModal) setShowDeactivateConfirmModal(false);
    },
    [onChange, removedPositions, secondaryPositionsFormName, setShowDeactivateConfirmModal],
  );

  const handleSecondaryPosition = useCallback(
    (e: ChangeEventType) => {
      const newPositionIds = filterUniquePositionIds(e.target.value).length;
      const prevPositionIds = filterUniquePositionIds(secondaryPositionIds).length;

      // if we have removed positions
      if (newPositionIds < prevPositionIds) {
        const filteredSelectedPosition = secondaryPositionIds.filter((value) =>
          e.target.value.includes(value),
        );

        const filteredDepartments = filteredSelectedPosition.filter((value) =>
          areAllPositionsSelected(value, e.target.value),
        );

        // Remove associated department node if it exists in the list of selected secondary positions
        const updatedRemovedPositions = filteredDepartments.filter((value) =>
          areAllPositionsSelected(value, e.target.value),
        );

        setRemovedPositions(updatedRemovedPositions);
        if (setShowDeactivateConfirmModal) {
          if (setNumberOfPositionsDeleted)
            setNumberOfPositionsDeleted(prevPositionIds - newPositionIds);
          setShowDeactivateConfirmModal(true);
        } else {
          onChange({
            target: {
              name: secondaryPositionsFormName,
              value: e.target.value,
            },
          });
        }
      } else {
        const updatedValue = [
          ...e.target.value,
          ...Object.entries(positionIdsByDepartments)
            .filter(([_, positionIds]) => {
              const filteredPositionIds = positionIds.filter((id) => id !== primaryPositionId);
              return (
                filteredPositionIds.length &&
                filteredPositionIds.every((id) => e.target.value.includes(id))
              );
            })
            .map(([department]) => department),
        ];

        onChange({
          ...e,
          target: {
            ...e.target,
            value: updatedValue,
          },
        });
      }
    },
    [
      areAllPositionsSelected,
      onChange,
      positionIdsByDepartments,
      primaryPositionId,
      secondaryPositionIds,
      secondaryPositionsFormName,
      setNumberOfPositionsDeleted,
      setShowDeactivateConfirmModal,
    ],
  );

  return {
    handlePrimaryPosition,
    handleSecondaryPosition,
    handleRemovedSecondaryPosition,
  };
};
